import { useEffect, useState } from "react";
import { X, Share, Plus, ArrowDown } from "lucide-react";

const DISMISS_KEY = "gpm_ios_install_dismissed_v1";
const DISMISS_DAYS = 7;

/** Persistent bottom banner shown ONLY on iOS Safari when not installed as a PWA.
 *  Shows a big animated arrow pointing at the browser's Share button and 3 tiny
 *  illustrations so first-time iPhone users know exactly what to tap. */
export default function IOSInstallBanner() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const ua = navigator.userAgent.toLowerCase();
        const isIOS = /iphone|ipad|ipod/.test(ua) && !/crios|fxios/.test(ua);
        if (!isIOS) return;
        // Already installed as standalone?
        // eslint-disable-next-line no-undef
        const isStandalone = window.navigator.standalone === true || window.matchMedia("(display-mode: standalone)").matches;
        if (isStandalone) return;
        // Recently dismissed?
        try {
            const raw = localStorage.getItem(DISMISS_KEY);
            if (raw) {
                const then = Number(raw);
                if (!Number.isNaN(then) && Date.now() - then < DISMISS_DAYS * 86400 * 1000) return;
            }
        } catch { /* ignore */ }
        // Delay slightly so it doesn't fight the first paint
        const id = setTimeout(() => setVisible(true), 1200);
        return () => clearTimeout(id);
    }, []);

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div
            className="fixed left-2 right-2 bottom-[68px] z-[70] rounded-2xl overflow-hidden shadow-2xl border border-gold-500/60 bg-gradient-to-br from-navy-500 to-navy-700 text-white animate-[slideUp_.4s_ease-out]"
            data-testid="ios-install-banner"
            style={{ animation: "slideUp .4s ease-out" }}
        >
            <button
                onClick={dismiss}
                aria-label="Dismiss"
                data-testid="ios-install-banner-dismiss"
                className="absolute top-2 right-2 p-1.5 rounded-full bg-white/10 text-white/80 hover:text-white hover:bg-white/20"
            >
                <X className="w-4 h-4" />
            </button>

            <div className="px-4 pt-3 pb-4">
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] uppercase tracking-widest text-gold-400 font-extrabold">iPhone Users</span>
                    <span className="w-1 h-1 rounded-full bg-gold-400" />
                    <span className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Add to Home Screen</span>
                </div>
                <h3 className="font-heading text-sm font-bold text-white leading-tight">
                    Install Global Property Mart in 3 taps
                </h3>

                {/* Steps: pill grid with icons */}
                <div className="mt-3 grid grid-cols-3 gap-2">
                    <Step num="1" icon={<Share className="w-4 h-4" />} label="Tap Share" />
                    <Step num="2" icon={<Plus className="w-4 h-4" />} label='"Add to Home Screen"' />
                    <Step num="3" icon={<span className="text-sm font-extrabold">✓</span>} label="Tap Add — done" />
                </div>

                {/* Animated pointer to Safari's bottom Share button */}
                <div className="mt-3 flex items-center justify-center gap-2 text-gold-400 text-[11px] font-bold">
                    Look for the
                    <Share className="w-3.5 h-3.5" />
                    icon at the bottom
                    <ArrowDown className="w-4 h-4 animate-bounce" />
                </div>
            </div>

            <style>{`
                @keyframes slideUp {
                    from { transform: translateY(120%); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
            `}</style>
        </div>
    );
}

function Step({ num, icon, label }) {
    return (
        <div className="flex flex-col items-center gap-1 rounded-xl bg-white/[0.06] border border-white/10 py-2 px-1">
            <div className="flex items-center gap-1 text-gold-400">
                <span className="text-[9px] font-extrabold uppercase tracking-widest">{num}</span>
                {icon}
            </div>
            <div className="text-[10px] text-white/85 text-center leading-tight px-0.5">{label}</div>
        </div>
    );
}
